import { useEffect, useRef, useCallback } from 'react'
import { createPopper, Instance, Options } from '@popperjs/core'
import { useTooltipContext } from './TooltipContext'

interface Parameters {
  delay: number
  disabled: boolean
  followingCursor?: boolean
  interactive: boolean
  options: Options
}

const showEvents = ['mouseenter', 'focus']
const hideEvents = ['mouseleave', 'blur']

const generateGetBoundingClientRect = (x = 0, y = 0) => {
  return () =>
    ({
      width: 0,
      height: 0,
      top: y,
      right: x,
      bottom: y,
      left: x,
    }) as DOMRect
}

export const usePopper = ({
  delay,
  disabled,
  interactive,
  options,
  followingCursor = false,
}: Parameters) => {
  const container = useRef<HTMLSpanElement>(null)
  const tooltip = useRef<HTMLDivElement>(null)
  const popperInstanceRef = useRef<Instance>(undefined)
  const { isOpen, setIsOpen } = useTooltipContext()
  const fixedDelay = delay === 0 ? 200 : delay
  const timerShowRef = useRef<NodeJS.Timeout>(undefined)
  const timerHideRef = useRef<NodeJS.Timeout>(undefined)

  const virtualElement = useRef({
    getBoundingClientRect: generateGetBoundingClientRect(),
  } as HTMLSpanElement)

  useEffect(() => {
    if (followingCursor) {
      document.addEventListener('mousemove', ({ clientX, clientY }) => {
        virtualElement.current.getBoundingClientRect = generateGetBoundingClientRect(
          clientX,
          clientY
        )
        popperInstanceRef.current?.update()
      })
    }
  }, [followingCursor, virtualElement])

  const show = useCallback(() => {
    timerShowRef.current = setTimeout(() => {
      setIsOpen(true)
      setTimeout(() => popperInstanceRef.current?.update())
    }, fixedDelay)
    clearTimeout(timerHideRef.current)
  }, [fixedDelay, popperInstanceRef, setIsOpen])

  const hide = useCallback(() => {
    clearTimeout(timerShowRef.current)
    timerHideRef.current = setTimeout(() => setIsOpen(false), fixedDelay)
  }, [setIsOpen, fixedDelay])

  useEffect(() => {
    if (container.current) {
      popperInstanceRef.current =
        !disabled && tooltip.current
          ? createPopper(
              followingCursor ? virtualElement.current : container.current,
              tooltip.current,
              options
            )
          : undefined
    }
  }, [isOpen, disabled, options, followingCursor])

  useEffect(() => {
    const setMultipleEvents = (
      eventList: string[],
      callback: () => void,
      setter: 'add' | 'remove' = 'add'
    ) =>
      eventList.forEach((event: string) => {
        const listenerType = `${setter}EventListener` as const
        container.current?.[listenerType](event, callback)
        if (interactive) {
          tooltip.current?.[listenerType](event, callback)
        }
      })

    setMultipleEvents(showEvents, show)
    setMultipleEvents(hideEvents, hide)
    return () => {
      setMultipleEvents(showEvents, show, 'remove')
      setMultipleEvents(hideEvents, hide, 'remove')
    }
  }, [container, tooltip, delay, disabled, interactive, setIsOpen, hide, show])

  useEffect(() => {
    setIsOpen(isOpen => (disabled ? false : isOpen))
  }, [disabled, setIsOpen])

  return {
    instance: popperInstanceRef,
    containerRef: container,
    tooltipRef: tooltip,
  }
}
